import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShoppingBag, Package, Receipt, ChartBar, X,
  FileText, FileMinus,
} from "lucide-react";
import { myApi } from "../api";
import Pagination from "../components/Pagination";

const PAYMENT_METHOD_LABELS = {
  MB: "Multibanco",
  MW: "MBWay",
  PP: "PayPal",
  CT: "Cartão",
  CP: "Cofidis",
  TB: "Transf. Bancária",
};
function paymentMethodLabel(id) {
  return PAYMENT_METHOD_LABELS[id] || id;
}

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);
const fmtPct = (v) =>
  new Intl.NumberFormat("pt-PT", { style: "percent", minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("pt-PT"); } catch { return d; }
};

// ─────────────── List ───────────────
export function MyOrders() {
  const [data, setData] = useState(null);
  const [states, setStates] = useState([]);
  const [estado, setEstado] = useState("");
  const [page, setPage] = useState(1);
  const [cancellingId, setCancellingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const pageSize = 20;

  const reload = () => {
    setData(null);
    myApi.orders(estado || undefined, page, pageSize)
      .then(setData)
      .catch(() => setData({ items: [], total: 0, total_pages: 0 }));
  };

  useEffect(() => { myApi.orderStates().then(setStates).catch(() => setStates([])); }, []);
  useEffect(() => {
    setData(null);
    myApi.orders(estado || undefined, page, pageSize)
      .then(setData)
      .catch(() => setData({ items: [], total: 0, total_pages: 0 }));
  }, [estado, page]);

  // Reset page to 1 when filter changes
  useEffect(() => { setPage(1); }, [estado]);

  const handleCancel = async (orderId, orderRef) => {
    if (cancellingId) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Tem a certeza que quer cancelar a encomenda #${orderRef}?\n\nEsta acção é irreversível. Os vales/créditos consumidos serão devolvidos automaticamente.`);
    if (!confirmed) return;
    setCancellingId(orderId);
    setErrorMsg("");
    try {
      const r = await myApi.orderCancel(orderId);
      if (r?.success) {
        reload();
      } else {
        setErrorMsg(r?.message || "Não foi possível cancelar a encomenda.");
      }
    } catch (e) {
      setErrorMsg(e?.message || "Erro ao cancelar a encomenda.");
    } finally {
      setCancellingId(null);
    }
  };

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 0;

  return (
    <div data-testid="my-orders-page" className="space-y-6">
      <div>
        <h1 className="text-4xl text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Encomendas
        </h1>
        <p className="text-sm text-[#6B7280]">Histórico completo de todas as suas encomendas IHTP.</p>
      </div>

      <div className="flex items-center flex-wrap gap-3">
        <label className="text-[10px] uppercase tracking-[0.25em] text-[#978D78] font-medium">Estado</label>
        <select
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          data-testid="my-orders-estado-filter"
          className="px-4 py-2 border border-[#E6E1D6] rounded-full text-sm bg-white text-[#0F1829] focus:outline-none focus:ring-2 focus:ring-[#978D78] min-w-[220px]"
        >
          <option value="">Todos os estados</option>
          {states.map((s) => <option key={s.ID} value={s.ID}>{s.Descricao}</option>)}
        </select>
        <div className="ml-auto text-xs text-[#978D78]">
          {data === null ? "A carregar..." : `${total} encomendas`}
        </div>
      </div>

      <div className="bg-white rounded-[20px] border border-[#E6E1D6] overflow-hidden">
        {errorMsg ? (
          <div className="px-5 py-3 bg-[#FEF2F2] border-b border-[#FECACA] text-sm text-[#991B1B]" data-testid="my-orders-error">
            {errorMsg}
          </div>
        ) : null}
        {data === null ? (
          <div className="p-10 text-center text-sm text-[#978D78]">A carregar...</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <ShoppingBag size={40} className="mx-auto text-[#978D78] mb-3" strokeWidth={1.3} />
            <div className="text-[#4B5563]">Nenhuma encomenda encontrada.</div>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-[#F5F2EB]">
                <tr>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">#</th>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Data</th>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Estado</th>
                  <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Total</th>
                  <th className="text-center px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Docs</th>
                  <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F5F2EB]">
                {items.map((o) => (
                  <tr
                    key={o.ID_Encomenda}
                    data-testid={`my-order-${o.ID_Encomenda}`}
                    className="hover:bg-[#FAF8F3] cursor-pointer"
                    onClick={() => (window.location.href = `/my/encomendas/${o.ID_Encomenda}`)}
                  >
                    <td className="px-5 py-3">
                      <Link to={`/my/encomendas/${o.ID_Encomenda}`} onClick={(e) => e.stopPropagation()} className="text-sm text-[#0F1829] hover:underline">
                        #{o.ID_Encomenda}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-sm text-[#4B5563]">{(o.DataEncomenda || "").slice(0, 16)}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[#F5F2EB] text-[#4B5563]">
                          {o.EstadoEncomenda || o.ID_EstadoEncomenda}
                        </span>
                        {o.ID_TipoPagamento ? (
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#0F1829]/5 border border-[#0F1829]/10 text-[#0F1829]"
                            data-testid={`my-order-paymethod-${o.ID_Encomenda}`}
                            title={paymentMethodLabel(o.ID_TipoPagamento)}
                          >
                            {paymentMethodLabel(o.ID_TipoPagamento)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right text-sm font-medium text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                      {fmt(o.PrecoFinal, o.ID_Moeda)}
                    </td>
                    {/* Documentos fiscais (Fatura / Recibo / Nota de Crédito) — apenas
                        os ícones que existem para a encomenda. Abrem em nova tab para
                        download directo do PDF gerado no Moloni. */}
                    <td className="px-5 py-3" data-testid={`my-order-docs-${o.ID_Encomenda}`}>
                      <div className="inline-flex items-center justify-center gap-2 w-full">
                        {o.UrlFatura ? (
                          <a
                            href={o.UrlFatura}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`Fatura ${o.NrFactura || ""}`}
                            data-testid={`my-order-doc-fatura-${o.ID_Encomenda}`}
                            className="text-[#0F1829] hover:text-[#978D78]"
                          ><FileText size={16} strokeWidth={1.8} /></a>
                        ) : null}
                        {o.UrlRecibo ? (
                          <a
                            href={o.UrlRecibo}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`Recibo ${o.NrRecibo || ""}`}
                            data-testid={`my-order-doc-recibo-${o.ID_Encomenda}`}
                            className="text-[#0F1829] hover:text-[#978D78]"
                          ><Receipt size={16} strokeWidth={1.8} /></a>
                        ) : null}
                        {o.UrlNotaCredito ? (
                          <a
                            href={o.UrlNotaCredito}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`Nota de Crédito ${o.NrNotaCredito || ""}`}
                            data-testid={`my-order-doc-nc-${o.ID_Encomenda}`}
                            className="text-[#B91C1C] hover:text-[#7F1D1D]"
                          ><FileMinus size={16} strokeWidth={1.8} /></a>
                        ) : null}
                        {!o.UrlFatura && !o.UrlRecibo && !o.UrlNotaCredito ? (
                          <span className="text-[10px] text-[#978D78]">—</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-3 justify-end">
                        {/* "Pay now" shortcut for orders still in AP — routes
                            to the dedicated payment page so the customer can
                            retrieve MB ref / PayPal link / etc. or change
                            method altogether. */}
                        {o.ID_EstadoEncomenda === "AP" && o.HashEncomenda ? (
                          <Link
                            to={`/my/orders/${encodeURIComponent(o.HashEncomenda)}/payment`}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`my-order-pay-${o.ID_Encomenda}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#0F1829] hover:text-[#978D78] underline underline-offset-2"
                          >Pagar agora</Link>
                        ) : null}
                        {/* "Cancel" — only for AP (Aguarda Pagamento). Vales e
                            créditos consumidos são devolvidos automaticamente
                            no backend (replica de cancelOrderInDB do legacy). */}
                        {o.ID_EstadoEncomenda === "AP" ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleCancel(o.ID_Encomenda, o.ID_Encomenda); }}
                            disabled={cancellingId === o.ID_Encomenda}
                            data-testid={`my-order-cancel-${o.ID_Encomenda}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#B91C1C] hover:text-[#7F1D1D] disabled:opacity-50 disabled:cursor-not-allowed underline underline-offset-2"
                          >
                            <X size={12} strokeWidth={2.2} />
                            {cancellingId === o.ID_Encomenda ? "A cancelar..." : "Cancelar"}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} testid="my-orders-pagination" />
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────── Detail (full) ───────────────
function Label({ children }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#978D78] bg-[#F5F2EB] text-center py-1.5 px-2 rounded mb-1">
      {children}
    </p>
  );
}
function Value({ children, className = "" }) {
  return <p className={`text-sm text-center text-[#0F1829] ${className}`}>{children}</p>;
}

export function MyOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    myApi.orderFullDetail(id).then((r) => { setData(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  const handleCancel = async () => {
    if (cancelling) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Tem a certeza que quer cancelar a encomenda #${id}?\n\nEsta acção é irreversível. Os vales/créditos consumidos serão devolvidos automaticamente.`);
    if (!confirmed) return;
    setCancelling(true);
    setErrorMsg("");
    try {
      const r = await myApi.orderCancel(Number(id));
      if (r?.success) {
        // recarregar detalhe para reflectir novo estado
        const fresh = await myApi.orderFullDetail(id);
        setData(fresh);
      } else {
        setErrorMsg(r?.message || "Não foi possível cancelar a encomenda.");
      }
    } catch (e) {
      setErrorMsg(e?.message || "Erro ao cancelar a encomenda.");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <div className="py-20 text-center text-sm text-[#978D78]">A carregar...</div>;
  }

  if (!data || !data.Detalhes) {
    return (
      <div data-testid="my-order-detail-empty">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-[#978D78] hover:text-[#0F1829] mb-4" data-testid="my-order-back-btn">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-10 text-center text-sm text-[#6B7280]">
          Não existem informações sobre esta encomenda.
        </div>
      </div>
    );
  }

  const d = data.Detalhes;
  const items = data.Itens || [];
  const states = data.Estados || [];
  const cur = d.ID_Moeda || "EUR";

  return (
    <div data-testid="my-order-detail-page" className="space-y-4">
      <button onClick={() => nav("/my/encomendas")} className="inline-flex items-center gap-2 text-sm text-[#978D78] hover:text-[#0F1829]" data-testid="my-order-back-btn">
        <ArrowLeft size={16} /> Voltar às encomendas
      </button>

      {errorMsg ? (
        <div className="px-4 py-3 bg-[#FEF2F2] border border-[#FECACA] rounded-[14px] text-sm text-[#991B1B]" data-testid="my-order-detail-error">
          {errorMsg}
        </div>
      ) : null}

      {/* Hero */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78] mb-1">Encomenda</div>
          <h1 className="text-3xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            #{d.ID_Encomenda}
          </h1>
          <div className="text-sm text-[#4B5563] mt-1">{fmtDate(d.DataEncomenda)}</div>
        </div>
        <div className="text-right">
          <span className="inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[#0F1829] text-white mb-2">
            {d.EstadoEncomenda}
          </span>
          <div className="text-3xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {fmt(d.PrecoFinal, cur)}
          </div>
          {d.ID_EstadoEncomenda === "AP" ? (
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="my-order-detail-cancel-btn"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#B91C1C] hover:text-white hover:bg-[#B91C1C] border border-[#B91C1C] px-3 py-1.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <X size={12} strokeWidth={2.4} />
              {cancelling ? "A cancelar..." : "Cancelar encomenda"}
            </button>
          ) : null}
        </div>
      </div>

      {/* Info block */}
      <div className="bg-white border border-[#E6E1D6] rounded-[20px] overflow-hidden" data-testid="my-order-info-section">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#F5F2EB] bg-[#FAF8F3]">
          <Receipt size={16} className="text-[#978D78]" />
          <h2 className="text-sm font-semibold text-[#0F1829]">Informações da encomenda</h2>
        </div>
        <div className="p-5 space-y-5">
          {d.ID_TipoPagamento === "MB" && (
            <div data-testid="mb-info" className="bg-[#F5F2EB] border border-[#978D78]/20 rounded-[14px] p-4">
              <Label>Informação de Pagamento Multibanco</Label>
              <div className="text-sm space-y-0.5 mt-2 text-center">
                <p><span className="font-semibold">Entidade:</span> {d.EntidadeMB}</p>
                <p><span className="font-semibold">Referência:</span> {d.ReferenciaMB}</p>
                <p><span className="font-semibold">Valor:</span> {fmt(d.PrecoFinal, cur)}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>ID de Encomenda</Label><Value className="font-mono font-semibold">{d.ID_Encomenda}</Value></div>
            <div><Label>Data da Encomenda</Label><Value>{fmtDate(d.DataEncomenda)}</Value></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Estado</Label><Value>{d.EstadoEncomenda}</Value></div>
            <div><Label>Tipo de Transporte</Label><Value>{d.DescricaoTipoTransporte || "—"}</Value></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><Label>Tipo de Pagamento</Label><Value>{d.DescricaoTipoPagamento || "—"}</Value></div>
            <div><Label>Data de Pagamento</Label><Value>{fmtDate(d.DataPagamento)}</Value></div>
            <div><Label>Data de Envio</Label><Value>{fmtDate(d.DataEnvio)}</Value></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Dados de Envio</Label>
              {d.moradaEnvioMorada ? (
                <div className="text-sm text-center text-[#0F1829] space-y-0.5">
                  <p>{d.moradaEnvioNome}</p><p>{d.moradaEnvioMorada}</p>
                  <p>{d.moradaEnvioCodigoPostal} {d.moradaEnvioLocalidade}</p>
                  <p>{d.moradaEnvioPais}</p>
                </div>
              ) : <Value className="text-[#6B7280] italic">Sem morada de envio</Value>}
            </div>
            <div>
              <Label>Dados de Faturação</Label>
              {d.moradaFaturacaoMorada ? (
                <div className="text-sm text-center text-[#0F1829] space-y-0.5">
                  <p>{d.moradaFaturacaoNome}</p>
                  {d.moradaFaturacaoNif && <p>NIF {d.moradaFaturacaoNif}</p>}
                  <p>{d.moradaFaturacaoMorada}</p>
                  <p>{d.moradaFaturacaoCodigoPostal} {d.moradaFaturacaoLocalidade}</p>
                  <p>{d.moradaFaturacaoPais}</p>
                </div>
              ) : <Value className="text-[#6B7280] italic">Sem morada de facturação</Value>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label>Desc. Crédito</Label><Value className="font-mono">{fmt(d.DescontoCredito, cur)}</Value></div>
            <div><Label>Desc. Total</Label><Value className="font-mono">{fmt(d.DescontoTotal, cur)}</Value></div>
            <div><Label>IVA</Label><Value className="font-mono">{fmt(d.IvaTotal, cur)}</Value></div>
            <div><Label>Portes</Label><Value className="font-mono">{fmt(d.CustoTransporte, cur)}</Value></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>NetPoints</Label><Value className="font-mono">{(d.NetPointsPessoal || 0).toFixed(2)}</Value></div>
            <div><Label>Subtotal (s/ IVA)</Label><Value className="font-mono">{fmt(d.PrecoTotal < 0 ? 0 : d.PrecoTotal, cur)}</Value></div>
            <div><Label>Total Final</Label><Value className="font-mono font-semibold text-[#0F1829]">{fmt(d.PrecoFinal, cur)}</Value></div>
          </div>
          {d.Observacao && (
            <div>
              <Label>Observações</Label>
              <Value className="text-[#6B7280]">{d.Observacao}</Value>
            </div>
          )}
        </div>
      </div>

      {/* Documentos fiscais (Fatura, Recibo, Nota de Crédito) — só renderiza se
          existir pelo menos um. Cada cartão abre o PDF Moloni numa nova tab. */}
      {(d.UrlFatura || d.UrlRecibo || d.UrlNotaCredito) && (
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] overflow-hidden" data-testid="my-order-docs-section">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-[#F5F2EB] bg-[#FAF8F3]">
            <FileText size={16} className="text-[#978D78]" />
            <h2 className="text-sm font-semibold text-[#0F1829]">Documentos fiscais</h2>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {d.UrlFatura ? (
              <a
                href={d.UrlFatura}
                target="_blank"
                rel="noreferrer"
                data-testid="my-order-detail-doc-fatura"
                className="flex items-center gap-3 px-4 py-3 rounded-[14px] border border-[#E6E1D6] hover:bg-[#FAF8F3] hover:border-[#0F1829]/30 transition-colors"
              >
                <FileText size={20} className="text-[#0F1829]" strokeWidth={1.7} />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Fatura</div>
                  <div className="text-sm text-[#0F1829] truncate">{d.NrFactura || "—"}</div>
                </div>
              </a>
            ) : null}
            {d.UrlRecibo ? (
              <a
                href={d.UrlRecibo}
                target="_blank"
                rel="noreferrer"
                data-testid="my-order-detail-doc-recibo"
                className="flex items-center gap-3 px-4 py-3 rounded-[14px] border border-[#E6E1D6] hover:bg-[#FAF8F3] hover:border-[#0F1829]/30 transition-colors"
              >
                <Receipt size={20} className="text-[#0F1829]" strokeWidth={1.7} />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Recibo</div>
                  <div className="text-sm text-[#0F1829] truncate">{d.NrRecibo || "—"}</div>
                </div>
              </a>
            ) : null}
            {d.UrlNotaCredito ? (
              <a
                href={d.UrlNotaCredito}
                target="_blank"
                rel="noreferrer"
                data-testid="my-order-detail-doc-nc"
                className="flex items-center gap-3 px-4 py-3 rounded-[14px] border border-[#FECACA] hover:bg-[#FEF2F2] hover:border-[#B91C1C]/40 transition-colors"
              >
                <FileMinus size={20} className="text-[#B91C1C]" strokeWidth={1.7} />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#B91C1C]">Nota de Crédito</div>
                  <div className="text-sm text-[#0F1829] truncate">{d.NrNotaCredito || "—"}</div>
                </div>
              </a>
            ) : null}
          </div>
        </div>
      )}

      {/* Items */}
      <div className="bg-white border border-[#E6E1D6] rounded-[20px] overflow-hidden" data-testid="my-order-items-section">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#F5F2EB] bg-[#FAF8F3]">
          <Package size={16} className="text-[#978D78]" />
          <h2 className="text-sm font-semibold text-[#0F1829]">Artigos ({items.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#FAF8F3]">
              <tr>
                <th className="text-left text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-4 py-2.5">Nome</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">IVA</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">Qtd</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">NetPoints</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">Créditos</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">P. Unit. (c/ IVA)</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">Total (c/ IVA)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F2EB]">
              {items.map((it, i) => (
                <tr key={it.ID_ItemEncomenda ?? `item-${i}`} data-testid={`my-order-item-${i}`} className="hover:bg-[#FAF8F3]">
                  <td className="px-4 py-2.5 text-sm text-[#0F1829]">{it.Descricao || "—"}</td>
                  <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{it.TaxaIva != null ? fmtPct(it.TaxaIva) : "—"}</td>
                  <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{it.Quantidade}</td>
                  <td className="px-3 py-2.5 text-sm text-center font-mono text-[#6B7280]">{it.TotalNetPoints ?? "—"}</td>
                  <td className="px-3 py-2.5 text-sm text-right font-mono text-[#6B7280]">{fmt(it.TotalCreditos, cur)}</td>
                  <td className="px-3 py-2.5 text-sm text-right font-mono text-[#6B7280]">{fmt(it.PrecoUnitarioComIva, cur)}</td>
                  <td className="px-3 py-2.5 text-sm text-right font-mono font-medium text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                    {fmt(it.Preco, cur)}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-[#6B7280]">Sem artigos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* States */}
      <div className="bg-white border border-[#E6E1D6] rounded-[20px] overflow-hidden" data-testid="my-order-states-section">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#F5F2EB] bg-[#FAF8F3]">
          <ChartBar size={16} className="text-[#978D78]" />
          <h2 className="text-sm font-semibold text-[#0F1829]">Evolução da encomenda ({states.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#FAF8F3]">
              <tr>
                <th className="text-left text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-4 py-2.5">Estado</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">Data</th>
                <th className="text-left text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">Observação</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] text-[#978D78] px-3 py-2.5">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F2EB]">
              {states.map((s, i) => (
                <tr key={`${s.EstadoEncomenda || 'state'}-${s.data || i}`} data-testid={`my-order-state-${i}`} className="hover:bg-[#FAF8F3]">
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full bg-[#F5F2EB] text-[#0F1829]">
                      {s.EstadoEncomenda}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{fmtDate(s.data)}</td>
                  <td className="px-3 py-2.5 text-sm text-[#6B7280]">{s.observacao || "—"}</td>
                  <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{s.enviado}</td>
                </tr>
              ))}
              {states.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-[#6B7280]">Sem estados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
